import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { ShieldAlert, Loader2 } from "lucide-react";
import axios from "axios";
import { embedToken, isEmbed, startEmbed } from "@/lib/embed";

const safeTarget = (to) => (to && to.startsWith("/") && !to.startsWith("//") && !to.startsWith("/embed") ? to : "/");
const consumed = new Set();

export default function Embed() {
  const { slug = "" } = useParams();
  const [sp] = useSearchParams();
  const [error, setError] = useState("");
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    if (isEmbed() && embedToken()) { window.location.replace(safeTarget(sp.get("to"))); return; }
    const exchange = async (token) => {
      if (!token || consumed.has(token)) return;
      consumed.add(token);
      try {
        const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/embed/session`, { token, slug }, { headers: { "X-Tenant-Slug": slug }, withCredentials: false });
        startEmbed(r.data.access_token, r.data.tenant?.id === "default" ? "" : r.data.tenant?.slug);
        window.location.replace(safeTarget(sp.get("to")));
      } catch (e) {
        setError(e?.response?.data?.detail || "Accesso non riuscito");
      }
    };
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const fromHash = hash.get("sso");
    if (fromHash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      exchange(fromHash);
      return;
    }
    setWaiting(true);
    let origins = null;
    let pending = null;
    const handle = (token, from) => {
      if (origins === null) { pending = { token, from }; return; }
      if (!origins.ok) { setError("Configurazione iframe non disponibile: riprova"); return; }
      if (origins.list.length && !origins.list.includes(from)) { setError(`Origine non autorizzata: ${from}`); return; }
      exchange(token);
    };
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/embed/config`, { params: { t: slug }, withCredentials: false })
      .then((r) => { origins = { ok: true, list: r.data.origins || [] }; })
      .catch(() => { origins = { ok: false, list: [] }; })
      .finally(() => { if (pending) handle(pending.token, pending.from); });
    const onMsg = (ev) => {
      if (!ev.data || ev.data.type !== "nouvelle-sso" || typeof ev.data.token !== "string") return;
      handle(ev.data.token, ev.origin);
    };
    window.addEventListener("message", onMsg);
    try { window.parent.postMessage({ type: "nouvelle-ready", slug }, "*"); } catch (e) { /* no parent */ }
    return () => window.removeEventListener("message", onMsg);
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6" data-testid="embed-page">
      <div className="max-w-md text-center">
        {error ? (
          <><ShieldAlert className="mx-auto h-10 w-10 text-rose-500" /><h1 className="mt-3 font-heading text-lg font-semibold text-slate-900">Accesso non riuscito</h1>
            <p className="mt-1 text-sm text-slate-600" data-testid="embed-error">{error}</p>
            <p className="mt-3 text-xs text-slate-400">Chiudi e riapri il pannello dall'applicativo per generare un nuovo accesso.</p></>
        ) : (
          <><Loader2 className="mx-auto h-8 w-8 animate-spin text-blue-600" /><p className="mt-3 text-sm text-slate-600" data-testid="embed-status">{waiting ? "In attesa dell'autenticazione dall'applicativo…" : "Accesso in corso…"}</p></>
        )}
      </div>
    </div>
  );
}
